import { useMutation } from '@apollo/client';
import { MetaData } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { ErrorBoundary } from '@sentry/react';
import { PickerFileMetadata } from 'filestack-js';
import {
  ChangeEventHandler,
  FC,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import { gengql } from '@/__generated__';
import { notify } from '@/actions/ui';
import { useAuth } from '@/auth';
import { isAdvancedUser } from '@/core/group';
import { channelUrlToId, isAnonymousUser } from '@/messaging/sendbird';

import { findOtherMember } from '../utils';
import s from './Chat.module.scss';
import ChatHeader, { UserNotAvailable } from './ChatHeader';
import MessageInput from './MessageInput';
import MessageList from './MessageList';

const CREATE_MESSAGING_ATTACHMENT = gengql(/* GraphQL */ `
  mutation chatSaveMessagingAttachment($channelUrl: String!, $fileUrl: String!) {
    saveMessagingAttachment(channel_url: $channelUrl, file_url: $fileUrl)
  }
`);

const CREATE_CHANNEL = gengql(/* GraphQL */ `
  mutation chatCreateChannel($userIds: [String!], $anonymous: Boolean) {
    createMessagingChannel(user_ids: $userIds, anonymous: $anonymous) {
      url
    }
  }
`);

interface ChatProps {
  channel: GroupChannel;
  metadata: MetaData;
  text?: string;
  isMobileVersion?: boolean;
  onBackToChannels?: () => void;
}

const MessagesUnavailable = () => (
  <div className={s.messagesUnavailable} role="status">
    Messages couldn&apos;t be loaded. Try reloading the page.
  </div>
);

const Chat: FC<ChatProps> = memo(
  ({
    channel,
    metadata,
    isMobileVersion = false,
    onBackToChannels,
    text: initialText = '',
  }: ChatProps) => {
    const navigate = useNavigate();

    const { viewer } = useAuth();
    if (!viewer.id) {
      throw new Error('Viewer must be logged in');
    }

    const [text, setText] = useState(initialText);
    const [messagesUnavailable, setMessagesUnavailable] = useState(false);

    const endTypingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
      return () => {
        if (endTypingTimeout.current) {
          clearTimeout(endTypingTimeout.current);
          channel.endTyping();
        }
      };
    }, [channel]);

    const [createMessagingAttachment] = useMutation(CREATE_MESSAGING_ATTACHMENT);
    const [createChannel] = useMutation(CREATE_CHANNEL);

    const handleMessageInput: ChangeEventHandler<HTMLInputElement> = useCallback(
      ({ target: { value } }) => {
        setText(value);

        if (!channel) return;

        if (endTypingTimeout.current) {
          clearTimeout(endTypingTimeout.current);
        }

        channel.startTyping();
        endTypingTimeout.current = setTimeout(() => {
          channel.endTyping();
        }, 3000);
      },
      [channel]
    );

    const handleMessageSend = useCallback(
      async (text: string, createAnonymousChannel: boolean) => {
        if (!channel) {
          throw new Error('Trying to send message with no channel');
        }

        await channel.endTyping();

        if (createAnonymousChannel) {
          const otherMember = findOtherMember(channel.members, viewer.id as string);
          if (!otherMember) {
            notify("Anonymous messaging isn't available for this conversation.", 'error');
            return;
          }
          const { data } = await createChannel({
            variables: { userIds: [viewer.id, otherMember.userId], anonymous: true },
          });
          const url = data!.createMessagingChannel.url;
          navigate(`/messaging/${channelUrlToId(url)}`);
          return;
        }

        try {
          channel.sendUserMessage({ message: text });
          setText('');

          // scrollToMessage(message);
        } catch (err) {
          notify('An error occurred when sending message', 'error');
          throw err;
        }
      },
      [channel, createChannel, navigate, viewer.id]
    );

    const handleAttachFile = useCallback(
      async (file: PickerFileMetadata) => {
        if (!channel) {
          throw new Error('Trying to send message with no channel');
        }

        const { data } = await createMessagingAttachment({
          variables: { channelUrl: channel.url, fileUrl: file.url },
        });

        const url = data!.saveMessagingAttachment!;

        channel.sendFileMessage({
          fileUrl: url,
          fileName: file.filename,
          fileSize: file.size,
          mimeType: file.mimetype,
        });
      },
      [channel, createMessagingAttachment]
    );

    const initialSendAnonymously =
      viewer.groups?.some((g) => g?.default_anonymous_messaging) || false;

    return (
      <div className={s.chat}>
        <Suspense>
          <ErrorBoundary
            fallback={
              <UserNotAvailable
                isMobileVersion={isMobileVersion}
                onBackToChannels={onBackToChannels}
              />
            }
          >
            <ChatHeader
              channel={channel}
              metadata={metadata}
              isMobileVersion={isMobileVersion}
              onBackToChannels={onBackToChannels}
            />
          </ErrorBoundary>
          {/* ENG-5628: errorPolicy handles the expected missing-counterpart case inside
              ChatMessage, but Apollo throws network errors regardless of errorPolicy, so
              a blip on the per-message sender lookup would still take the whole Messages
              page to the route error element. Confine it to the message area — the header
              keeps working, and the composer below disables rather than sending into a
              pane that can no longer render — and let Sentry hear about it. */}
          <ErrorBoundary
            fallback={<MessagesUnavailable />}
            onError={() => setMessagesUnavailable(true)}
          >
            <MessageList channel={channel} metadata={metadata} />
          </ErrorBoundary>
        </Suspense>
        {/* Disabled once the boundary above trips: the composer outlives MessageList, but
            the collection feeding onMessagesAdded unmounts with it, so a send would appear
            to succeed while nothing rendered and markAsRead had stopped. Reloading — what
            the fallback asks for — is the only recovery. */}
        <MessageInput
          disabled={messagesUnavailable}
          text={text}
          isFocused={true}
          isMobileVersion={isMobileVersion}
          onChange={handleMessageInput}
          onSend={handleMessageSend}
          isAnonymous={isAnonymousUser(metadata, viewer.id)}
          canSendAnonymously={isAdvancedUser(viewer)}
          onAttachFile={handleAttachFile}
          initialSendAnonymously={initialSendAnonymously}
        />
      </div>
    );
  }
);
Chat.displayName = 'Chat';

export default Chat;
